import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "../../components/Button";
import { WALKTHROUGH_STOPS } from "./walkthroughStops";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
/* v3's auto-play pace: long enough to read a stop out loud. */
const AUTO_MS = 7500;

/**
 * The 2-minute walkthrough of the pathway, full screen. A brand-new participant must take it once (`required`): there is
 * no close button, no Escape and no skip, only Back and Next through to "Start my pathway". Replayed from the hub it can
 * be closed at any point. Every picture is sample data; nothing here reads or changes the account.
 */
export function Walkthrough({
  required,
  onFinish,
  onClose,
}: {
  required: boolean;
  /** The last "Start my pathway". May reject (e.g. offline), in which case the walkthrough stays open with the error. */
  onFinish: () => Promise<void> | void;
  /** Replays only: the Close button and Escape. */
  onClose?: () => void;
}) {
  const stops = WALKTHROUGH_STOPS;
  const [index, setIndex] = useState(-1); // -1 is the welcome intro
  const [auto, setAuto] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const closable = !required && Boolean(onClose);
  const last = index === stops.length - 1;

  const go = useCallback(
    (next: number) => setIndex(Math.max(0, Math.min(stops.length - 1, next))),
    [stops.length],
  );

  const finish = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await onFinish();
    } catch (failure) {
      setError(
        failure instanceof Error && failure.message
          ? failure.message
          : "We couldn't save that. Check your connection and try again.",
      );
      setBusy(false);
    }
  }, [onFinish]);

  // Focus moves in on open and goes back where it was on close; the page behind doesn't scroll.
  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = overflow;
      before?.focus?.();
    };
  }, []);

  // The intro's button, then Next once the walkthrough starts.
  const started = index >= 0;
  useEffect(() => {
    box.current?.querySelector<HTMLElement>("[data-primary]")?.focus();
  }, [started]);
  // Back on the first stop disables itself; don't leave focus stranded on it.
  useEffect(() => {
    const active = document.activeElement as HTMLButtonElement | null;
    if (!active || active === document.body || active.disabled)
      box.current?.querySelector<HTMLElement>("[data-primary]")?.focus();
  }, [index]);

  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (closable) closeRef.current?.();
        return;
      }
      if (index >= 0 && !busy && event.key === "ArrowRight" && !last) go(index + 1);
      if (index >= 0 && !busy && event.key === "ArrowLeft") go(index - 1);
      if (event.key !== "Tab" || !box.current) return;
      const items = Array.from(box.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (!items.length) return;
      const firstItem = items[0]!;
      const lastItem = items[items.length - 1]!;
      if (event.shiftKey && document.activeElement === firstItem) {
        event.preventDefault();
        lastItem.focus();
      } else if (!event.shiftKey && document.activeElement === lastItem) {
        event.preventDefault();
        firstItem.focus();
      } else if (!box.current.contains(document.activeElement)) {
        event.preventDefault();
        firstItem.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [closable, index, last, busy, go]);

  // Auto-play walks forward and stops on the last stop, so finishing is always the participant's own tap.
  useEffect(() => {
    if (!auto || index < 0) return;
    if (last) {
      setAuto(false);
      return;
    }
    const timer = setTimeout(() => go(index + 1), AUTO_MS);
    return () => clearTimeout(timer);
  }, [auto, index, last, go]);

  const stop = index >= 0 ? stops[index]! : null;

  return (
    <div
      ref={box}
      role="dialog"
      aria-modal="true"
      aria-label="Walkthrough of the Growth Pathway"
      className="walkthrough fixed inset-0 z-[70] overflow-y-auto bg-bg"
    >
      {closable ? (
        <div className="sticky top-0 z-10 flex justify-end px-[var(--content-gutter)] pt-3">
          <Button variant="ghost" size="sm" className="bg-bg" onClick={onClose}>
            Close
          </Button>
        </div>
      ) : null}

      {!stop ? (
        <div className="mx-auto flex min-h-full max-w-[720px] flex-col justify-center px-4 py-12 sm:px-8">
          <div className="text-[12px] font-bold uppercase tracking-[0.08em] text-kicker">
            Welcome to your Growth Pathway
          </div>
          <h1 className="walkthrough-in mt-2 text-[clamp(40px,8vw,72px)] leading-[1.02] text-accent">
            Here's the road <span className="text-kicker">ahead.</span>
          </h1>
          <p className="mt-4 max-w-[560px] text-[17px] text-text-muted">
            Before you start, take this 2-minute walkthrough of the six steps. It uses a sample
            participant, so nothing you tap here changes your account.
          </p>
          <div className="mt-7">
            <Button data-primary onClick={() => go(0)}>
              Start the walkthrough →
            </Button>
          </div>
        </div>
      ) : (
        <div className="mx-auto flex min-h-full max-w-[880px] flex-col gap-5 px-4 py-6 sm:px-8 sm:py-10">
          <div
            className="h-2 overflow-hidden rounded-full bg-surface-high"
            role="progressbar"
            aria-label="Walkthrough progress"
            aria-valuenow={index + 1}
            aria-valuemin={1}
            aria-valuemax={stops.length}
          >
            <i
              className="block h-full bg-accent transition-[width] duration-300 ease-[var(--ease)]"
              style={{ width: `${((index + 1) / stops.length) * 100}%` }}
            />
          </div>
          <div aria-live="polite" key={index} className="walkthrough-in">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-[12px] font-bold uppercase tracking-[0.08em] text-kicker">
                {stop.kicker}
              </span>
              <span className="text-[13px] font-bold text-text-muted">
                {index + 1} of {stops.length}
              </span>
            </div>
            <h2 className="mt-1.5 text-[clamp(26px,4.5vw,36px)] leading-[1.1] text-accent">
              {stop.title}
            </h2>
            <p className="mt-2 max-w-[640px] text-[16px] text-text">{stop.desc}</p>
          </div>
          <div key={`v${index}`} className="walkthrough-in" aria-hidden="true">
            {stop.visual()}
          </div>
          {error ? (
            <p role="alert" className="m-0 text-negative">
              {error}
            </p>
          ) : null}
          <div className="mt-auto flex flex-wrap items-center gap-2.5 pt-2">
            <Button
              variant="ghost"
              size="sm"
              disabled={index === 0 || busy}
              onClick={() => go(index - 1)}
            >
              ← Back
            </Button>
            <Button
              data-primary
              size="sm"
              disabled={busy}
              onClick={() => (last ? void finish() : go(index + 1))}
            >
              {last ? "Start my pathway →" : "Next →"}
            </Button>
            {!last ? (
              <Button
                variant="ghost"
                size="sm"
                aria-pressed={auto}
                onClick={() => setAuto((on) => !on)}
              >
                {auto ? "❚❚ Pause" : "▶ Auto-play"}
              </Button>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
